import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import { useSignalR } from "src/hooks";
import {
  EditActionItemNoteDto,
  FetchError,
  queryKeys,
  updateActionItemDetail,
  updateActionItemInPlans,
  updateActionItemInUserOwnershipLists,
  updateActionItemSummary,
  useAccessToken,
} from "src/http";

type Variables = {
  actionItemGuid: string;
  note: string;
  planGuid: string;
};

const useEditActionItemNoteRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editActionItemNoteRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditActionItemNoteDto = {
      note: variables.note,
    };

    const response = await fetch(`${configuration.apiRootUrl}/action-items/${variables.actionItemGuid}/note`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "PUT",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editActionItemNoteRequest };
};

const useEditActionItemNote = () => {
  const { editActionItemNoteRequest } = useEditActionItemNoteRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editActionItemNoteRequest,
    onMutate: async (variables) => {
      const updateFn = (item: any) => ({ ...item, note: variables.note });

      const cachedSummaryData = await updateActionItemSummary(queryClient, variables.actionItemGuid, updateFn);
      const cachedDetailData = await updateActionItemDetail(queryClient, variables.actionItemGuid, updateFn);
      const cachedPlanData = await updateActionItemInPlans(queryClient, variables.actionItemGuid, updateFn);
      const cachedUserStrategyData = await updateActionItemInUserOwnershipLists(
        queryClient,
        variables.actionItemGuid,
        updateFn,
      );

      return { cachedSummaryData, cachedDetailData, cachedPlanData, cachedUserStrategyData };
    },
    onSuccess: (_data, variables) => {
      signalR.sendRefetchActionItem(variables.actionItemGuid);
      signalR.sendRefetchComments(variables.actionItemGuid);
      signalR.sendRefetchPlan(variables.planGuid);
    },
    onError: (_error, variables, context) => {
      if (context?.cachedSummaryData) {
        queryClient.setQueryData(queryKeys.actionItems.summary(variables.actionItemGuid), context.cachedSummaryData);
      }
      if (context?.cachedDetailData) {
        queryClient.setQueryData(queryKeys.actionItems.detail(variables.actionItemGuid), context.cachedDetailData);
      }
      if (context?.cachedPlanData) {
        context.cachedPlanData.forEach((data, key) => queryClient.setQueryData(key, data));
      }
      if (context?.cachedUserStrategyData) {
        context.cachedUserStrategyData.forEach((data, key) => queryClient.setQueryData(key, data));
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.actionItems.item(variables.actionItemGuid));
      queryClient.invalidateQueries(queryKeys.actionItems.userOwnershipLists());
      queryClient.invalidateQueries(queryKeys.plans.getPlan(variables.planGuid));
      queryClient.invalidateQueries(queryKeys.plans.getPlanSubitems(variables.planGuid));
    },
  });
};

export { useEditActionItemNote };
